// Order-details helpers (ported from old `utils/document-check-utils.ts` +
// `utils/orderDetails.ts`).

import type { IApiFieldMatch } from '@/api/certified-materials/types';
import type { IDocumentContentCheck, IVerificationField } from '../-types';

export function formatAddress(
  address?: string | null,
  city?: string | null,
  postCode?: string | null,
  country?: string | null,
): string {
  return [address, city, postCode, country].filter(Boolean).join(', ');
}

export function transformFieldMatchesToDocumentCheck(
  fieldMatches: IApiFieldMatch[],
): IDocumentContentCheck {
  const documents = fieldMatches.map(match => ({
    id: match.id,
    documentId: match.documentId,
    documentName: match.fileName,
    documentType: match.documentType,
    companyName: '',
    hasIssues: match.status === 'error',
    // TEMP (CMMM-145 Phase 3): filter out per-requirement standards entries
    // until they are rendered as the per-standard UI. The engine emits these
    // ALONGSIDE the aggregate `standards` verdict on multi-requirement docs,
    // so without this a two-standard order shows three near-identical rows
    // (Standards, Standards (OCS), Standards (RCS)) and the per-document
    // tally counts each one. V1 does the same in
    // `views/certified-materials/utils/document-check-utils.ts`; the filter
    // was lost when this transform was ported. Keep the two in step.
    verificationFields: match.fields
      .filter(field => field.field !== 'standards_per_requirement')
      .map(
        (field): IVerificationField => ({
          name: field.label,
          // Prefer the backend's unique per-finding key so accepting one
          // article's finding doesn't validate the same category on every
          // other article.
          fieldKey: field.key ?? field.field,
          status:
            field.status === 'valid'
              ? 'match'
              : field.status === 'needs_review'
                ? 'needs_review'
                : field.status === 'skipped'
                  ? 'skipped'
                  : 'mismatch',
          expectedValue: field.expected,
          extractedValue: field.actual,
          reasoning: field.reasoning,
          isManuallyValidated: field.isManuallyValidated ?? false,
          validatedByUserName: field.validatedByUserName ?? null,
          validatedAt: field.validatedAt ?? null,
        }),
      ),
    comments: match.comments,
  }));

  const documentsWithMismatches = documents.filter(d => d.hasIssues).length;
  const documentsMatching = documents.length - documentsWithMismatches;

  return {
    status:
      documents.length === 0
        ? 'pending'
        : documentsWithMismatches > 0
          ? 'needs_review'
          : 'approved',
    received: documents.length,
    total: documents.length,
    documentsWithMismatches,
    documentsMatching,
    documents,
  };
}
